import { useState } from 'react';
import type { RoutingSmartOverviewResponse, SmartTargetState } from '@nadoutong/contracts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Chip, ConfirmDialog, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import { useResetRoutingSmart } from '../../api/useRoutingSmart.ts';
import { TenantRoutingSmartShareChart } from './TenantRoutingSmartShareChart.tsx';
import {
  formatPosterior,
  formatRatio,
  formatRefireSaveWindows,
  formatTtftPair,
  formatUsd,
  groupSmartOverviewTargets,
  isSmartState,
} from './smartRouting.ts';

const STATE_TONE: Record<SmartTargetState, ChipTone> = {
  warming: 'warn',
  serving: 'ok',
  quarantined: 'bad',
  half_open: 'warn',
  retired: 'neutral',
};

const STATE_LABEL: Record<SmartTargetState, I18nKey> = {
  warming: 'tenant_routing.smart.state.warming',
  serving: 'tenant_routing.smart.state.serving',
  quarantined: 'tenant_routing.smart.state.quarantined',
  half_open: 'tenant_routing.smart.state.half_open',
  retired: 'tenant_routing.smart.state.retired',
};

function windowTriple(m1: number, h1: number, day1: number, asShare: boolean): string {
  const fmt = asShare ? formatRatio : (value: number) => String(value);
  return `${fmt(m1)} / ${fmt(h1)} / ${fmt(day1)}`;
}

/**
 * Learning-state table + share stack + reset. Polls overview every 10s
 * while the parent keeps the query enabled.
 */
export function TenantRoutingSmartOverview({
  skuId,
  skuName,
  overview,
  loading,
  failed,
  onRetry,
}: {
  readonly skuId: string;
  readonly skuName: string;
  readonly overview: RoutingSmartOverviewResponse | undefined;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const reset = useResetRoutingSmart();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const rows = overview?.targets ?? [];
  const groups = groupSmartOverviewTargets(rows);
  const stale = failed && overview !== undefined;
  const firstFail = failed && overview === undefined;
  const hostLabel = overview?.runtime?.host;
  const hydratedAt = overview?.runtime?.hydrated_at;
  const folded = overview?.folded_configs;

  return (
    <div
      className="flex flex-col gap-3 border-t border-line-soft/50 pt-3"
      data-testid={`tenant-routing-smart-overview-${skuId}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4">
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="text-label text-paper">{t('tenant_routing.smart.overview.title')}</span>
          {hostLabel !== undefined && hostLabel !== '' ? (
            <span
              className="text-dense text-paper-low"
              data-testid={`tenant-routing-smart-overview-host-${skuId}`}
            >
              {t('tenant_routing.smart.overview.host', { host: hostLabel })}
              {hydratedAt !== undefined && hydratedAt !== '' ? ` · ${hydratedAt}` : ''}
            </span>
          ) : null}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={reset.isPending}
          onClick={() => setConfirmOpen(true)}
          data-testid={`tenant-routing-smart-reset-${skuId}`}
        >
          {t('tenant_routing.smart.reset')}
        </Button>
      </div>

      {(overview?.warnings ?? []).some((row) => row.rule === 'self_pool_unprobeable') ? (
        <div
          role="status"
          className="mx-4 rounded-lg border border-warn-line bg-warn-dim/50 px-3 py-2"
          data-testid={`tenant-routing-smart-overview-unprobeable-${skuId}`}
        >
          <span className="text-dense text-warn">{t('tenant_routing.smart.warning.self_pool_unprobeable')}</span>
        </div>
      ) : null}

      {stale ? (
        <div
          role="status"
          className="mx-4 flex items-center justify-between gap-3 rounded-lg border border-warn-line bg-warn-dim/50 px-3 py-2"
          data-testid={`tenant-routing-smart-overview-stale-${skuId}`}
        >
          <span className="text-dense text-warn">{t('tenant_routing.smart.overview.stale')}</span>
          <Button size="sm" onClick={onRetry} data-testid={`tenant-routing-smart-overview-retry-${skuId}`}>
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {loading && overview === undefined ? (
        <p
          className="m-0 px-4 text-dense text-paper-low"
          data-testid={`tenant-routing-smart-overview-loading-${skuId}`}
        >
          {t('tenant_routing.smart.overview.loading')}
        </p>
      ) : firstFail ? (
        <div
          role="alert"
          className="mx-4 flex items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-dim px-3 py-2"
          data-testid={`tenant-routing-smart-overview-error-${skuId}`}
        >
          <span className="text-dense text-bad-bright">{t('tenant_routing.smart.overview.error')}</span>
          <Button size="sm" onClick={onRetry} data-testid={`tenant-routing-smart-overview-retry-${skuId}`}>
            {t('common.retry')}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p
          className="m-0 px-4 text-dense text-paper-low"
          data-testid={`tenant-routing-smart-overview-empty-${skuId}`}
        >
          {t('tenant_routing.smart.overview.empty')}
        </p>
      ) : (
        <Table
          className="rounded-none border-0 bg-transparent"
          tableClassName="table-fixed min-w-[1100px]"
          testId={`tenant-routing-smart-overview-table-${skuId}`}
        >
          <THead>
            <Tr interactive={false}>
              <Th>{t('tenant_routing.default_channels.upstream_channel')}</Th>
              <Th num>{t('tenant_routing.smart.overview.dials')}</Th>
              <Th num>{t('tenant_routing.smart.overview.share')}</Th>
              <Th num>{t('tenant_routing.smart.overview.first_dial')}</Th>
              <Th num>{t('tenant_routing.smart.overview.refire_saved')}</Th>
              <Th num>{t('tenant_routing.smart.overview.ttft')}</Th>
              <Th num>{t('tenant_routing.smart.overview.cost_per_1k')}</Th>
              <Th num>{t('tenant_routing.smart.overview.posterior')}</Th>
              <Th>{t('tenant_routing.smart.overview.probe')}</Th>
              <Th>{t('tenant_routing.smart.overview.state')}</Th>
            </Tr>
          </THead>
          <TBody>
            {groups.flatMap((group) => {
              const header = (
                <Tr
                  key={`cfg-${group.key}`}
                  interactive={false}
                  data-testid={`tenant-routing-smart-overview-config-${group.key}`}
                >
                  <Td colSpan={10}>
                    <span className="text-dense text-paper">
                      {group.configId === null
                        ? t('tenant_routing.smart.overview.default_pool')
                        : t('tenant_routing.smart.overview.config', {
                          name: group.configName ?? group.configId,
                          id: group.configId,
                        })}
                    </span>
                  </Td>
                </Tr>
              );
              const body = group.rows.map((row) => {
              const state: SmartTargetState = isSmartState(row.state) ? row.state : 'serving';
              return (
                <Tr
                  key={row.target_hash}
                  interactive={false}
                  data-testid={`tenant-routing-smart-overview-row-${row.target_hash}`}
                >
                  <Td mono>
                    <span className="block truncate" title={row.channel_slug ?? row.upstream_id}>
                      {row.channel_slug ?? row.upstream_id}
                    </span>
                  </Td>
                  <Td num>{windowTriple(row.dials.m1, row.dials.h1, row.dials.day1, false)}</Td>
                  <Td num>{windowTriple(row.share.m1, row.share.h1, row.share.day1, true)}</Td>
                  <Td num>
                    {windowTriple(
                      row.first_dial_success.m1,
                      row.first_dial_success.h1,
                      row.first_dial_success.day1,
                      true,
                    )}
                  </Td>
                  <Td num>{formatRefireSaveWindows(row.refire_saved, row.refires)}</Td>
                  <Td num>{formatTtftPair(row.ttft_ms.p50, row.ttft_ms.p95)}</Td>
                  <Td num>{formatUsd(row.cost_per_1k_usd)}</Td>
                  <Td num>
                    {formatPosterior(row.posterior.mean, row.posterior.lo, row.posterior.hi)}
                  </Td>
                  <Td>
                    {row.probe === null ? (
                      <span className="text-paper-low">—</span>
                    ) : (
                      <span className={row.probe.ok ? 'text-ok' : 'text-bad'}>
                        {row.probe.ok
                          ? t('tenant_routing.smart.probe.ok')
                          : t('tenant_routing.smart.probe.fail')}
                        {' · '}
                        {row.probe.kind}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Chip tone={STATE_TONE[state]} lamp={false}>
                      {t(STATE_LABEL[state])}
                    </Chip>
                  </Td>
                </Tr>
              );
              });
              return [header, ...body];
            })}
          </TBody>
        </Table>
      )}
      {folded !== undefined && folded.config_count > 0 ? (
        <p
          className="m-0 px-4 text-dense text-paper-low"
          data-testid={`tenant-routing-smart-overview-folded-${skuId}`}
        >
          {t('tenant_routing.smart.overview.folded', {
            configs: String(folded.config_count),
            targets: String(folded.target_count),
          })}
        </p>
      ) : null}

      <TenantRoutingSmartShareChart
        skuId={skuId}
        minutes={overview?.decisions_per_minute ?? []}
        targets={rows}
        ready={overview !== undefined}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!reset.isPending) setConfirmOpen(open);
        }}
        title={t('tenant_routing.smart.reset_title')}
        body={t('tenant_routing.smart.reset_body')}
        confirmLabel={t('tenant_routing.smart.reset_confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone="danger"
        loading={reset.isPending}
        typeToConfirm={skuName}
        typeToConfirmLabel={t('tenant_routing.smart.reset_type_label')}
        testId={`tenant-routing-smart-reset-confirm-${skuId}`}
        onConfirm={() => {
          void reset.mutateAsync({ skuId }).then(
            () => {
              setConfirmOpen(false);
              toast.success(t('tenant_routing.smart.toast_reset'));
            },
            (error: unknown) => {
              toast.error(coerceErrorMessage(error));
            },
          );
        }}
      />
    </div>
  );
}
